import { DateTime } from 'luxon';
import { isEmpty } from 'lodash';

import { YardTaskCheckEntity, YardTaskInventoryGroupType } from '@nova/core';

import type {
  ChecklistItem,
  Maybe,
  Nullable,
  TimeInput,
  YardTaskInventoryGroupInput
} from '@nova/core';

const VALID_YARD_TASK_CHECK_RELATED_ENTITIES = new Set<string>(Object.values(YardTaskCheckEntity));

/**
 * Maps checklist items from form format to API format.
 * The mapper assumes that the checklist is an array of objects with a description and complete property.
 * If the complete property is not present, it defaults to false.
 * @param checklist - Form checklist items
 * @returns API checklist format
 */
export function mapChecklist(checklist?: ChecklistItem[]): ChecklistItem[] {
  if (!Array.isArray(checklist)) {
    return [];
  }

  if (checklist.length === 0) {
    return [];
  }

  /**
   * At this point, the checklist has been validated, so the reduce is just to
   * remove a possibly trailing empty item
   */
  return checklist.reduce((acc, item) => {
    if (isEmpty(item.description)) {
      return acc;
    }

    return [
      ...acc,
      {
        complete: item.complete || false,
        description: item.description
      }
    ];
  }, [] as ChecklistItem[]);
}

/**
 * Maps inventory-group selections from form state to API request format.
 * `spotArea` groups carry the spotAreaId in `value`; `missing` / `unassigned`
 * carry no value. Invalid/empty entries are dropped and duplicates removed.
 * @param groups - Inventory groups from form state
 * @returns Normalized inventory groups for the API payload
 */
export function mapInventoryGroups(
  groups?: Maybe<YardTaskInventoryGroupInput[]>
): YardTaskInventoryGroupInput[] {
  if (!Array.isArray(groups)) {
    return [];
  }

  const validTypes = new Set<YardTaskInventoryGroupType>(Object.values(YardTaskInventoryGroupType));
  const seen = new Set<string>();

  return groups.reduce((acc, group) => {
    if (!group || !validTypes.has(group.type)) {
      return acc;
    }

    const isSpotArea = group.type === YardTaskInventoryGroupType.SpotArea;
    const value = isSpotArea ? group.value || null : null;

    if (isSpotArea && !value) {
      return acc;
    }

    const key = `${group.type}:${value ?? ''}`;
    if (seen.has(key)) {
      return acc;
    }
    seen.add(key);

    return [...acc, isSpotArea ? { type: group.type, value } : { type: group.type }];
  }, [] as YardTaskInventoryGroupInput[]);
}

/**
 * Maps related entity and ID based on subject type
 * @param subject - Subject type
 * @param spot - Spot ID
 * @param area - Area ID
 * @param asset - Asset ID
 * @param warehouseId - Warehouse ID (used as relatedId for warehouse-scoped inventory checks)
 * @returns Object with relatedEntity and relatedId
 */
export function mapRelatedEntity(
  subject: string,
  spot?: Maybe<string>,
  area?: Maybe<string>,
  asset?: Maybe<string>,
  warehouseId?: Maybe<string>
) {
  if (subject !== '' && !VALID_YARD_TASK_CHECK_RELATED_ENTITIES.has(subject)) {
    throw new Error(`Invalid relatedEntity: ${subject}`);
  }

  const relatedEntity = subject;
  let relatedId: Nullable<string> = null;

  if (relatedEntity === YardTaskCheckEntity.Spot) {
    relatedId = spot || null;
  } else if (relatedEntity === YardTaskCheckEntity.SpotArea) {
    relatedId = area || null;
  } else if (relatedEntity === YardTaskCheckEntity.Warehouse) {
    relatedId = warehouseId || null;
  } else {
    relatedId = asset || null;
  }

  return { relatedEntity, relatedId };
}

/**
 * Transforms due date time from API format to form state format
 * @param dueDateTime - ISO date string from API
 * @param warehouseTimezone - Warehouse timezone
 * @returns Object with dueByDate and dueByTime
 */
export function transformDueDatePayloadToState(
  dueDateTime: string,
  warehouseTimezone: string
): {
  dueByDate: string;
  dueByTime: TimeInput;
} {
  if (!dueDateTime) {
    return {
      dueByDate: '',
      dueByTime: {
        time: '',
        amPm: 'pm'
      }
    };
  }

  const date = DateTime.fromISO(dueDateTime).setZone(warehouseTimezone);
  const dueByDate = date.isValid ? date.toFormat('yyyy-MM-dd') : '';
  const dueByTime = {
    time: date.isValid ? date.toFormat('hh:mm') : '',
    amPm: date.isValid ? (date.hour >= 12 ? 'pm' : 'am') : 'pm'
  };

  return {
    dueByDate,
    dueByTime
  };
}
